import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ServiceToastForm from './ServiceToastForm';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { createShallowCopy } from '../../../../utils/helpFunctions';
import { useDispatch, useSelector } from 'react-redux';
import {
    clientDataStore,
    createClientDataStore,
    getClientDataStore,
    oneClient,
} from '../../../../redux/slices/clientsSlice';
import { toast } from 'react-toastify';
import MultinationalTabs from '../../../../components/MultinationalTabs';
import { Col, Row, Tooltip } from 'antd';
import CustomMultiselect from '../../../../components/CustomMultiselect';
import { languagesOptions as languages } from '../../../../shared/data/clientDetailsOptions';

const languagesOptions = languages.filter((el) => el.id !== 'IS');
const excludedClientLanguagesCodes = ['en'];

export default function ClientWebAppSubPage() {
    const dispatch = useDispatch();
    const generalData = useSelector(clientDataStore);
    const client = useSelector(oneClient);

    const [excludedCodes, setExcludedCodes] = useState([]);
    const [data, setData] = useState({
        is_show_service_toast: false,
        service_toast_translations: {
            sv: '',
            fi: '',
            da: '',
            no: '',
            en: '',
        },
        client_languages: { sv: [], fi: [], da: [], no: [], is: [] },
    });

    useEffect(() => {
        setData({
            is_show_service_toast: generalData?.is_show_service_toast || false,
            service_toast_translations: {
                sv: generalData?.service_toast_translations?.sv || '',
                fi: generalData?.service_toast_translations?.fi || '',
                da: generalData?.service_toast_translations?.da || '',
                no: generalData?.service_toast_translations?.no || '',
                en: generalData?.service_toast_translations?.en || '',
            },
            client_languages: {
                sv:
                    generalData?.client_languages?.sv ||
                    languagesOptions.map((el) => el.id),
                fi:
                    generalData?.client_languages?.fi ||
                    languagesOptions.map((el) => el.id),
                da:
                    generalData?.client_languages?.da ||
                    languagesOptions.map((el) => el.id),
                no:
                    generalData?.client_languages?.no ||
                    languagesOptions.map((el) => el.id),
                is:
                    generalData?.client_languages?.is ||
                    languagesOptions.map((el) => el.id),
            },
        });
    }, [
        generalData.is_show_service_toast,
        generalData.service_toast_translations,
        generalData?.client_languages,
    ]);

    useEffect(() => {
        if (!!Object.keys(generalData).length) {
            const excludedCodes = languagesOptions
                .filter((option) => option.id !== 'EN')
                .filter(
                    (option) =>
                        !generalData?.account_languages?.includes(option.id),
                )
                .map((item) => item.id.toLowerCase());

            setExcludedCodes(excludedCodes);
        }
    }, [generalData]);

    const [arrow] = useState('Show');

    const mergedArrow = useMemo(() => {
        if (arrow === 'Hide') {
            return false;
        }

        if (arrow === 'Show') {
            return true;
        }

        return {
            pointAtCenter: true,
        };
    }, [arrow]);

    const onInputChange = (e) => {
        const { name, value } = e.target;
        const dataCopy = createShallowCopy(data);

        dataCopy.service_toast_translations[name] = value;

        setData(dataCopy);
    };

    const onSwitchChange = (e) => {
        const dataCopy = createShallowCopy(data);

        dataCopy.is_show_service_toast = e;

        setData(dataCopy);
    };

    const onSubmit = async () => {
        const fields = [];

        Object.keys(data).forEach((el) =>
            fields.push({
                key_name: el,
                value: data[el],
            }),
        );

        await dispatch(
            createClientDataStore({
                id: client.client_uid,
                body: { fields },
                collectionName: 'clients',
            }),
        ).unwrap();

        await dispatch(
            getClientDataStore({
                id: client.client_uid,
                collectionName: 'clients',
            }),
        ).unwrap();

        toast.success('Changes saved successfully');
    };

    const onMultiselectChange = useCallback(
        (options, selectName, flag) => {
            const opts = options.map((el) => el.value);

            setData({
                ...data,
                [selectName]: { ...data[selectName], [flag]: opts },
            });
        },
        [data],
    );

    const createSelectOptions = (value, options) =>
        value.reduce((acc, el) => {
            for (const option of options) {
                if (option.id.includes(el)) acc.push(option);
            }
            return acc;
        }, []);

    return (
        <div>
            <ServiceToastForm
                data={data}
                onInputChange={onInputChange}
                onSwitchChange={onSwitchChange}
                excludedCodes={excludedCodes}
            />
            <div className="form__item-wrapper">
                <Row>
                    <Col span={9}>
                        <div className="form__item-wrapper">
                            <p className="input-label">
                                Languages{' '}
                                <Tooltip
                                    placement="top"
                                    title="First language in the list is set as default"
                                    arrow={mergedArrow}
                                    overlayClassName="ant-tooltip-custom"
                                >
                                    <QuestionCircleOutlined />
                                </Tooltip>
                            </p>
                            <MultinationalTabs
                                excludedCodes={excludedClientLanguagesCodes}
                                renderWithFlag={(flag) => (
                                    <CustomMultiselect
                                        onChange={(option) =>
                                            onMultiselectChange(
                                                option,
                                                'client_languages',
                                                flag,
                                            )
                                        }
                                        options={languagesOptions}
                                        placeholder="Select languages"
                                        value={createSelectOptions(
                                            data?.client_languages?.[flag],
                                            languagesOptions,
                                        )}
                                        hideSelectedOptions={false}
                                        closeMenuOnSelect={false}
                                        showAllTab={false}
                                    />
                                )}
                            />
                        </div>
                    </Col>
                </Row>
            </div>
            <div className="form__footer">
                <button
                    className="modal__button modal__button-accept"
                    type="button"
                    onClick={onSubmit}
                >
                    Save
                </button>
            </div>
        </div>
    );
}
